'use client';

import LensImage from '@/components/LensImage';
import ProductItemTag from '@/components/Product/ProductItemTag';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { isShowMemberOnlyTag } from '@/utils/tools';
import { useTranslations } from 'next-intl';

// 支付页面产品项的数据类型
interface PaymentProductItem {
  id: number;
  type?: number;
  qty: number;
  formattedPrice: string;
  hasColor?: boolean;
  product_info: {
    name: string;
    color_name?: string;
    color?: string;
    is_glass: number;
    image_url?: string;
    image_gray?: string;
    img?: string;
    image?: string;
    background_image_of_mirror_leg?: string;
    frame_background_image?: string;
    product_group?: any;
    activity_style?: {
      product_item_rules_text?: string;
    };
  };
  lens_info: any;
  option?: {
    prescription_type?: string;
    prescription_type_name?: string;
  };
}

interface ProductListItemProps {
  /** 产品数据 */
  item: PaymentProductItem;
  /** 选中的促销API列表 */
  selectedListFormPriceApi?: any[]; // 从计价接口获取的商品信息，用于订正 seckill 标签显示不正确

  imageUrl: string;
}

/**
 * 支付页面产品列表项组件
 * 用于显示支付页面的产品信息，包含图片、标签、名称、颜色、处方类型、数量和价格
 */
export default function ProductListItem({
  item,
  selectedListFormPriceApi = [],
  imageUrl,
}: ProductListItemProps) {
  const t = useTranslations('cart.GiftsWithCartItem');
  const userInfo = useStore((state) => state.userInfo);
  const getType = (): 3 | 4 | 5 | 6 | 7 | 'extra' | 'member_only' => {
    const { product_group } = item.product_info;
    const member_only = isShowMemberOnlyTag({
      userInfo,
      landingPage: {},
      productGroup: product_group,
    });
    if (item.type === 6) return; // 积分兑换订单不显示标签
    try {
      if (selectedListFormPriceApi.length) {
        for (const ele of selectedListFormPriceApi) {
          if (
            (ele.id === item.id &&
              ele.products_promotions_show === 1 &&
              ele.products_promotions_qty > 0 &&
              Number(ele.products_promotions_type) === 3) ||
            (ele.products_promotions_show === 2 && Number(ele.products_promotions_type) === 3)
          ) {
            return 6;
          }
        }
      }
    } catch (err) {
      logger.log(err);
    }
    if (item.id === 0) {
      return 'extra';
    }
    if (member_only) {
      return 'member_only';
    }
    return item.type as 3 | 4 | 5 | 6 | 7 | 'extra' | 'member_only';
  };

  const getLabelText = () => {
    if (item.type === 6) return '';
    try {
      if (selectedListFormPriceApi.length) {
        for (const ele of selectedListFormPriceApi) {
          if (
            (ele.id === item.id &&
              ele.products_promotions_show === 1 &&
              ele.products_promotions_qty > 0 &&
              [3, 4].includes(Number(ele.products_promotions_type))) ||
            (ele.products_promotions_show === 2 &&
              [3, 4].includes(Number(ele.products_promotions_type)))
          ) {
            return ele.product_info.activity_style.product_item_rules_text || 'Seckill';
          }
        }
      }
    } catch (err) {
      logger.log(err);
    }
    return '';
  };

  return (
    <div className="w-full flex  justify-between py-16 border-t border-solid border-[#e2e2e2] text-14">
      <div className=" mr-12 w-[5.2vw] h-[5.2vw] min-w-[70px] min-h-[70px] flex items-center justify-center bg-[#eee] ">
        <LensImage
          product={item.product_info}
          imageUrl={imageUrl || item.product_info.image}
          lens={item.lens_info}
          isGlass={+item.product_info.is_glass === 1}
          onlyShowImage
        />
        <span className="sr-only">{t('zeeloolGlasses')}</span>
      </div>
      <div className="flex-1">
        <ProductItemTag type={getType()} labelText={getLabelText()} className="mb-10" />
        <div className="mb-10">{item.product_info.name}</div>
        {item.hasColor && (
          <p className="mb-10">
            {t('color')}: {item.product_info.color_name || item.product_info.color}
          </p>
        )}

        {item.option &&
          item.product_info.is_glass === 1 &&
          item.option.prescription_type &&
          item.option.prescription_type !== EPrescriptionType.FRAME_ONLY && (
            <p className="mb-10">RX: {item.option.prescription_type_name}</p>
          )}

        {item.qty && item.formattedPrice && (
          <p className="flex items-center justify-between">
            <span>
              {t('qty')}: {item.qty}
            </span>
            <span>{item.formattedPrice}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// 导出类型供外部使用
export type { PaymentProductItem, ProductListItemProps };
